/**
 * YNS Tab Router
 * Handles URL hash-based navigation for the Education Hub tabs.
 *
 * Features:
 * - URL hash routing (#videos, #pdfs, #study)
 * - Browser history navigation (back/forward)
 * - Tab switching with animations
 * - Default tab handling
 * - Touch/swipe support for mobile
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    defaultTab: 'today',
    validTabs: ['today', 'library', 'books', 'faq', 'contact', 'course', 'account', 'videos', 'pdfs', 'qbank', 'study', 'fasttrack'],
    selectors: {
      hub: '#ynsEducationHub',
      desktopNav: '#desktopTabNav',
      mobileNav: '#mobileTabNav',
      tabButtons: '.yns-tab-btn',
      tabPanels: '.yns-tab-panel'
    },
    classes: {
      activeTab: 'active',
      activePanel: 'active',
      hidden: 'hidden'
    },
    animation: {
      duration: 300,
      easing: 'ease-out'
    }
  };

  // ============================================
  // State
  // ============================================
  let currentTab = null;
  let isTransitioning = false;
  let touchStartX = 0;
  let touchEndX = 0;

  // ============================================
  // DOM References
  // ============================================
  let hub = null;
  let desktopNav = null;
  let mobileNav = null;
  let tabButtons = [];
  let tabPanels = [];

  // ============================================
  // Initialization
  // ============================================
  function init() {
    // Get DOM elements
    hub = document.querySelector(CONFIG.selectors.hub);
    if (!hub) {
      console.warn('YNS Tab Router: Hub element not found');
      return;
    }

    desktopNav = document.querySelector(CONFIG.selectors.desktopNav);
    mobileNav = document.querySelector(CONFIG.selectors.mobileNav);
    tabButtons = Array.from(document.querySelectorAll(CONFIG.selectors.tabButtons));
    tabPanels = Array.from(document.querySelectorAll(CONFIG.selectors.tabPanels));

    if (tabButtons.length === 0 || tabPanels.length === 0) {
      console.warn('YNS Tab Router: No tabs or panels found');
      return;
    }

    // Setup event listeners
    setupTabListeners();
    setupHistoryListener();
    setupSwipeListeners();

    // Initialize from URL or default
    const initialTab = getTabFromHash() || CONFIG.defaultTab;
    switchTab(initialTab, false); // Don't push to history on initial load

  }

  // ============================================
  // Tab Switching
  // ============================================
  function switchTab(tabId, pushHistory = true) {
    // Validate tab
    if (!CONFIG.validTabs.includes(tabId)) {
      console.warn(`YNS Tab Router: Invalid tab "${tabId}"`);
      tabId = CONFIG.defaultTab;
    }

    // Skip if already on this tab or transitioning
    if (tabId === currentTab || isTransitioning) {
      return;
    }

    isTransitioning = true;
    const previousTab = currentTab;
    currentTab = tabId;

    // Update URL hash
    if (pushHistory) {
      window.history.pushState({ tab: tabId }, '', `#${tabId}`);
    } else {
      window.history.replaceState({ tab: tabId }, '', `#${tabId}`);
    }

    // Update tab buttons
    tabButtons.forEach(button => {
      const isActive = button.dataset.tab === tabId;
      button.classList.toggle(CONFIG.classes.activeTab, isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Animate panel transition
    animatePanelTransition(previousTab, tabId);

    // Dispatch custom event for other modules to react
    hub.dispatchEvent(new CustomEvent('yns:tabchange', {
      detail: {
        previousTab,
        currentTab: tabId,
        timestamp: Date.now()
      },
      bubbles: true
    }));
  }

  function animatePanelTransition(fromTab, toTab) {
    const fromPanel = fromTab ? document.getElementById(`panel-${fromTab}`) : null;
    const toPanel = document.getElementById(`panel-${toTab}`);

    if (!toPanel) {
      console.warn(`YNS Tab Router: Panel for tab "${toTab}" not found`);
      isTransitioning = false;
      return;
    }

    // Determine animation direction based on tab order
    const fromIndex = fromTab ? CONFIG.validTabs.indexOf(fromTab) : -1;
    const toIndex = CONFIG.validTabs.indexOf(toTab);
    const direction = toIndex > fromIndex ? 1 : -1;

    // Hide all panels first
    tabPanels.forEach(panel => {
      panel.classList.remove(CONFIG.classes.activePanel);
      panel.setAttribute('hidden', '');
    });

    // Prepare and show target panel with animation
    toPanel.removeAttribute('hidden');
    toPanel.style.opacity = '0';
    toPanel.style.transform = `translateX(${direction * 20}px)`;

    // Force reflow
    toPanel.offsetHeight;

    // Animate in
    toPanel.style.transition = `opacity ${CONFIG.animation.duration}ms ${CONFIG.animation.easing}, transform ${CONFIG.animation.duration}ms ${CONFIG.animation.easing}`;
    toPanel.style.opacity = '1';
    toPanel.style.transform = 'translateX(0)';
    toPanel.classList.add(CONFIG.classes.activePanel);

    // Cleanup after animation
    setTimeout(() => {
      toPanel.style.transition = '';
      toPanel.style.opacity = '';
      toPanel.style.transform = '';
      isTransitioning = false;

      // Scroll to top of panel
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Focus management for accessibility
      const firstFocusable = toPanel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        // Don't auto-focus on mobile to prevent keyboard popup
        if (window.innerWidth > 768) {
          firstFocusable.focus({ preventScroll: true });
        }
      }
    }, CONFIG.animation.duration);
  }

  // ============================================
  // Event Handlers
  // ============================================
  function setupTabListeners() {
    // Desktop and mobile tab clicks
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const tabId = button.dataset.tab;
        if (tabId) {
          e.preventDefault();
          switchTab(tabId);
        }
      });

      // Keyboard navigation
      button.addEventListener('keydown', (e) => {
        handleTabKeydown(e, button);
      });
    });
  }

  function handleTabKeydown(e, currentButton) {
    const currentIndex = tabButtons.indexOf(currentButton);
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        newIndex = currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = tabButtons.length - 1;
        break;
      default:
        return;
    }

    // Focus and activate the new tab
    const newButton = tabButtons[newIndex];
    if (newButton) {
      newButton.focus();
      const tabId = newButton.dataset.tab;
      if (tabId) {
        switchTab(tabId);
      }
    }
  }

  function setupHistoryListener() {
    window.addEventListener('popstate', (e) => {
      const tabId = e.state?.tab || getTabFromHash() || CONFIG.defaultTab;
      switchTab(tabId, false);
    });

    // Also listen for hashchange (for direct URL changes)
    window.addEventListener('hashchange', () => {
      const tabId = getTabFromHash() || CONFIG.defaultTab;
      if (tabId !== currentTab) {
        switchTab(tabId, false);
      }
    });
  }

  function setupSwipeListeners() {
    if (!hub) return;

    // Only enable swipe on mobile
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) return;

    hub.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    hub.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    }, { passive: true });
  }

  function handleSwipe() {
    const swipeThreshold = 100; // Minimum swipe distance
    const diff = touchStartX - touchEndX;

    if (Math.abs(diff) < swipeThreshold) return;

    const currentIndex = CONFIG.validTabs.indexOf(currentTab);

    if (diff > 0) {
      // Swipe left - go to next tab
      if (currentIndex < CONFIG.validTabs.length - 1) {
        switchTab(CONFIG.validTabs[currentIndex + 1]);
      }
    } else {
      // Swipe right - go to previous tab
      if (currentIndex > 0) {
        switchTab(CONFIG.validTabs[currentIndex - 1]);
      }
    }
  }

  // ============================================
  // URL Hash Utilities
  // ============================================
  function getTabFromHash() {
    const hash = window.location.hash.replace('#', '');
    return CONFIG.validTabs.includes(hash) ? hash : null;
  }

  // ============================================
  // Public API
  // ============================================
  window.YNSTabRouter = {
    switchTab: switchTab,
    getCurrentTab: () => currentTab,
    getValidTabs: () => [...CONFIG.validTabs],

    // Programmatic navigation
    goToVideos: () => switchTab('videos'),
    goToPdfs: () => switchTab('pdfs'),
    goToStudy: () => switchTab('study'),
    goToFasttrack: () => switchTab('fasttrack'),

    // Navigation helpers
    goToNext: () => {
      const currentIndex = CONFIG.validTabs.indexOf(currentTab);
      if (currentIndex < CONFIG.validTabs.length - 1) {
        switchTab(CONFIG.validTabs[currentIndex + 1]);
      }
    },
    goToPrevious: () => {
      const currentIndex = CONFIG.validTabs.indexOf(currentTab);
      if (currentIndex > 0) {
        switchTab(CONFIG.validTabs[currentIndex - 1]);
      }
    },

    // Event subscription
    onTabChange: (callback) => {
      if (hub) {
        hub.addEventListener('yns:tabchange', (e) => callback(e.detail));
      }
    }
  };

  // ============================================
  // Initialize on DOM Ready
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
